import { createGlobalStyle } from 'styled-components';

import {
  DEFAULT_STRAIN_TYPE_PILL_BACKGROUND,
  getStrainTypePillTextColor,
} from '@dutchie/common/utils/strain-type-colors';

/**
 * CSS custom property definitions for the core-menu UI.
 *
 * Two layers:
 *   1. --dct--customized-*  Dutchie Customized Theme — 1:1 mappings of
 *      theme.customized leaves.  This is the internal source of truth.
 *   2. --dcm-{scope}-{property}  Core-Menu tokens — the externally
 *      controllable surface.  Remote themes override these on :root.
 *      Where possible dcm vars reference dct vars so the customized
 *      theme flows through automatically.
 *
 * --- Remaining JS theme.customized usages (not convertible to CSS vars) ---
 *
 * TinyColor.mix() -- derives new colors at runtime, must stay JS:
 *   - components/order-row/reorder-order-row.tsx           TinyColor.mix(buttonsLinks, '#fff', 85)
 *   - components/widgets/rewards/cash-redemption.tsx        tinycolor.mix(buttonsLinks, '#000000', 50)
 *   - components/widgets/rewards/discount-redemption.tsx    tinycolor.mix(buttonsLinks, '#000000', 50) x3
 *   - components/widgets/loyalty-tiers/loyalty-tiers.tsx    tinycolor.mix(buttonsLinks, '#000000', 50) x3
 *   - components/product-card-v2/views/card-view.tsx        TinyColor.mix(buttonsLinks, '#000', 20) x2
 *   - components/progress-button/progress-button.tsx        TinyColor.mix(buttonsLinks, '#fff'|'#000', 10) x2
 *
 * Hex alpha appending -- appends raw hex alpha to color string, incompatible with var():
 *   - components/product-card-v2/views/card-view.tsx                     buttonsLinks + '1a' (10%)
 *   - components/core/text-input/places-autocomplete-input.jsx           buttonsLinks + '66' (40%)
 *   - components/carousels/.../buy-it-again-list-item.tsx                buttonsLinks + '1a' (10%)
 *
 * productCardButton enum -- JS enum comparison, not a CSS value:
 *   - components/product-card-v2/views/card-view.tsx                     === ProductCardButtonOption.tinted x2
 *
 * utils.isNavBarDark boolean ternaries -- selects colors based on boolean:
 *   - dispensary/core-menu/components/header/placeholder-navigation/placeholder-navigation.styles.ts  x4
 *   - dispensary/core-menu/components/header/mobile-header/mobile-header.styles.ts                    x1
 *   - dispensary/core-menu/components/header/desktop-header/desktop-header.styles.ts                  x2
 *   - dispensary/core-menu/components/header/menu-type/menu-type.tsx                                  x1
 *
 * progress-button.tsx -- destructured customized from theme inside css`` template:
 *   - radius.buttons usage migrated to var(), TinyColor calls stay JS (listed above)
 *
 * --- Verified NOT a duplicate (do not "fix" this) ---
 *   --dcm-account-popover-logout-color and --dcm-checkout-link-color are hardcoded to the
 *   literal #0b99e6. This looks like it should dedupe to var(--dcm-color-focus), but
 *   --dcm-color-focus resolves to theme.colors.blue[55] (#4c94d7) -- a different color that
 *   only shares the "blue, used for focus/links" role, not the value. Swapping these to
 *   var(--dcm-color-focus) would silently change their rendered color. Confirmed via
 *   theme/constants/colors.ts before leaving these as literals.
 *
 * --- Intentional behavior change: closing --dcm-color-surface/-border "leaks" ---
 *   The ten vars below used to hardcode the literal #ffffff/#c8d2db directly, so a remote
 *   SDK theme overriding --dcm-color-surface or --dcm-color-border on :root (the documented,
 *   only way to reskin this file today -- see the module doc above) did NOT reach them; they
 *   silently kept the platform default while everything else recolored. Routing them through
 *   var(--dcm-color-surface)/var(--dcm-color-border) instead is the entire point of this
 *   dedupe: it closes that leak, so any existing remote theme's surface/border override now
 *   also reaches these surfaces, same as every other --dcm-color-surface/-border consumer
 *   already covered by CoreMenuVariables. Default rendering (no override present) is
 *   byte-for-byte unchanged -- verified in core-menu-variables.test.tsx, which also pins the
 *   canonical #ffffff/#c8d2db values these aliases depend on. Affected: --dcm-popover-bg,
 *   --dcm-login-nudge-bg, --dcm-brands-card-bg, --dcm-cart-offers-bg, --dcm-added-to-cart-bg,
 *   --dcm-checkout-payment-icon-bg, --dcm-infobar-bg, --dcm-storefront-header-bg (surface),
 *   --dcm-cart-btn-bg-empty, --dcm-account-popover-divider-color (border).
 */
export const CoreMenuVariables = createGlobalStyle<{ $isPlum?: boolean }>`
  :root {
    /* ================================================================
      DUTCHIE CUSTOMIZED THEME (dct)
      1:1 CSS custom property definitions for every theme.customized leaf.
    ================================================================ */

    /* colors */
    --dct--customized-colors-buttonsLinks: ${({ theme }) => theme.customized.colors.buttonsLinks};
    --dct--customized-colors-navBar: ${({ theme }) => theme.customized.colors.navBar};
    --dct--customized-colors-staffPickTag: ${({ theme }) => theme.customized.colors.staffPickTag};
    --dct--customized-colors-discountTag: ${({ theme }) => theme.customized.colors.discountTag};

    /* fonts */
    --dct--customized-fonts-primary: ${({ theme }) => theme.customized.fonts.primary};
    --dct--customized-fonts-secondary: ${({ theme }) => theme.customized.fonts.secondary};

    /* textTransforms */
    --dct--customized-textTransforms-buttons: ${({ theme }) => theme.customized.textTransforms.buttons};
    --dct--customized-textTransforms-links: ${({ theme }) => theme.customized.textTransforms.links};
    --dct--customized-textTransforms-tags: ${({ theme }) => theme.customized.textTransforms.tags};

    /* radius */
    --dct--customized-radius-xs: ${({ theme }) => theme.customized.radius?.xs ?? '2px'};
    --dct--customized-radius-sm: ${({ theme }) => theme.customized.radius?.sm ?? '4px'};
    --dct--customized-radius-md: ${({ theme }) => theme.customized.radius?.md ?? '8px'};
    --dct--customized-radius-lg: ${({ theme }) => theme.customized.radius?.lg ?? '12px'};
    --dct--customized-radius-xl: ${({ theme }) => theme.customized.radius?.xl ?? '16px'};
    --dct--customized-radius-tags: ${({ theme }) => theme.customized.radius?.tags ?? '4px'};
    --dct--customized-radius-cards: ${({ theme }) => theme.customized.radius?.cards ?? '12px'};
    --dct--customized-radius-buttons: ${({ theme }) => theme.customized.radius?.buttons ?? '4px'};

    /* maxSiteWidth — stored WITH px unit for direct use */
    --dct--customized-maxSiteWidth: ${({ theme }) => theme.customized.maxSiteWidth}px;

    /* ================================================================
       CORE-MENU TOKENS (dcm)
       Externally controllable layer. Remote themes can override any
       --dcm-* variable via their own GlobalStyles on :root.
       ================================================================ */

    /* ----------------------------------------------------------------
       GLOBAL — Colors: Core Palette
       ---------------------------------------------------------------- */
    --dcm-color-primary: var(--dct--customized-colors-buttonsLinks);
    --dcm-color-navbar-bg: var(--dct--customized-colors-navBar);
    --dcm-color-staff-pick-tag: var(--dct--customized-colors-staffPickTag);
    --dcm-color-discount-tag: var(--dct--customized-colors-discountTag);
    --dcm-color-body-bg: #ffffff;
    --dcm-color-surface: #ffffff;
    --dcm-color-surface-hover: ${({ theme }) => theme.colors.grey[95]};
    --dcm-color-surface-muted: #f5f6f7;
    --dcm-color-text-primary: #485055;
    /* The theme's own body text color, captured on :root. CMS containers re-point
       --dcm-color-text-primary for their descendants; this alias keeps the original so an
       element that paints its own opaque theme surface can pair text with that surface. */
    --dcm-color-text-default: var(--dcm-color-text-primary);
    --dcm-color-text-secondary: #a0a7ab;
    --dcm-color-text-inverted: #ffffff;
    --dcm-color-text-muted: ${({ theme }) => theme.colors.grey[45]};
    --dcm-color-border: #c8d2db;
    --dcm-color-border-dark: #2c2c2c;
    --dcm-color-focus: ${({ theme }) => theme.colors.blue[55]};
    --dcm-color-focus-shadow: #0b99e666;

    /* ----------------------------------------------------------------
       GLOBAL — Colors: Status
       ---------------------------------------------------------------- */
    --dcm-color-status-open: ${({ theme, $isPlum }) => ($isPlum ? `#4d2a3d` : theme.colors.green[55])};
    --dcm-color-status-closed: ${({ theme }) => theme.colors.red[55]};
    --dcm-color-status-preorder: #a0a7ab;
    --dcm-color-status-coming-soon: ${({ theme }) => theme.colors.orange[65]};

    /* ----------------------------------------------------------------
       GLOBAL — Typography
       ---------------------------------------------------------------- */
    --dcm-font-primary: var(--dct--customized-fonts-primary);
    --dcm-font-secondary: var(--dct--customized-fonts-secondary);
    --dcm-font-size-xs: 12px;
    --dcm-font-size-sm: 13px;
    --dcm-font-size-md: 14px;
    --dcm-font-size-lg: 16px;
    --dcm-font-size-xl: 18px;
    --dcm-font-size-2xl: 20px;
    --dcm-font-weight-normal: 400;
    --dcm-font-weight-semibold: 600;
    --dcm-font-weight-bold: 700;
    --dcm-line-height-normal: 1.43;

    /* ----------------------------------------------------------------
       GLOBAL — Spacing Scale
       ---------------------------------------------------------------- */
    --dcm-space-1: 4px;
    --dcm-space-2: 8px;
    --dcm-space-3: 12px;
    --dcm-space-4: 16px;
    --dcm-space-5: 20px;
    --dcm-space-6: 24px;

    /* ----------------------------------------------------------------
       GLOBAL — Border Radius
       ---------------------------------------------------------------- */
    --dcm-radius-xs: var(--dct--customized-radius-xs);
    --dcm-radius-sm: var(--dct--customized-radius-sm);
    --dcm-radius-md: var(--dct--customized-radius-md);
    --dcm-radius-lg: var(--dct--customized-radius-lg);
    --dcm-radius-xl: var(--dct--customized-radius-xl);
    --dcm-radius-tags: var(--dct--customized-radius-tags);
    --dcm-radius-buttons: var(--dct--customized-radius-buttons);
    --dcm-radius-cards: var(--dct--customized-radius-cards);

    /* ----------------------------------------------------------------
       DESKTOP HEADER
       ---------------------------------------------------------------- */
    --dcm-header-bg: var(--dcm-color-navbar-bg);
    --dcm-header-text-color: ${({ theme }) =>
      theme.customized.utils.isNavBarDark ? '#fff' : 'var(--dcm-color-text-primary)'};
    --dcm-header-border-color: var(--dcm-color-border);
    --dcm-header-border-width: 1px;
    --dcm-header-padding-x: 20px;
    --dcm-header-gap: 20px;
    --dcm-header-max-width: 1240px;
    --dcm-header-min-height: 68px;
    --dcm-header-logo-max-width: 120px;
    --dcm-header-logo-height: 44px;
    --dcm-header-divider-color: ${({ theme }) =>
      theme.customized.utils.isNavBarDark ? 'var(--dcm-color-border-dark)' : 'var(--dcm-color-border)'};
    --dcm-header-divider-height: 36px;
    --dcm-header-shadow: none;
    --dcm-header-z-index: 10;

    /* ----------------------------------------------------------------
       MOBILE HEADER
       ---------------------------------------------------------------- */
    --dcm-header-mobile-padding-x: 12px;
    --dcm-header-mobile-padding-y: 8px;
    --dcm-header-mobile-gap: 8px;
    --dcm-header-mobile-menu-btn-font-size: var(--dcm-font-size-md);
    --dcm-header-mobile-menu-btn-font-weight: var(--dcm-font-weight-semibold);
    --dcm-header-mobile-menu-btn-gap: 4px;
    --dcm-header-mobile-logo-height: 32px;

    /* ----------------------------------------------------------------
       NAVIGATION LINKS
       ---------------------------------------------------------------- */
    --dcm-nav-link-color: inherit;
    --dcm-nav-link-hover-color: var(--dcm-color-primary);
    --dcm-nav-link-active-color: var(--dcm-color-primary);
    --dcm-nav-link-font-size: var(--dcm-font-size-md);
    --dcm-nav-link-font-weight: var(--dcm-font-weight-bold);
    --dcm-nav-link-padding: 10px;
    --dcm-nav-link-gap: 20px;
    --dcm-nav-link-transition: all 0.2s;
    --dcm-nav-min-height: 20px;
    --dcm-nav-heading-font-size: var(--dcm-font-size-lg);
    --dcm-nav-heading-font-weight: var(--dcm-font-weight-bold);
    --dcm-header-nav-title-text-color: var(--dcm-color-text-primary);
    --dcm-nav-two-column-width: 504px;
    --dcm-nav-divider-color: var(--dcm-color-border);
    --dcm-nav-divider-height: 34px;
    --dcm-nav-active-border-color: var(--dcm-color-primary);

    /* ----------------------------------------------------------------
       POPOVER
       ---------------------------------------------------------------- */
    --dcm-popover-bg: var(--dcm-color-surface);
    --dcm-popover-border-radius: 8px;
    --dcm-popover-shadow: 0 5px 15px 0 #00000033;
    --dcm-popover-padding: 8px 0;
    --dcm-popover-min-width: 187px;
    --dcm-popover-margin-top: 8px;
    --dcm-popover-item-color: #47494c;
    --dcm-popover-item-hover-bg: var(--dcm-color-surface-hover);
    --dcm-popover-item-font-size: var(--dcm-font-size-md);
    --dcm-popover-item-padding: 12px 24px;
    --dcm-popover-item-font-weight: var(--dcm-font-weight-normal);
    --dcm-popover-item-active-font-weight: var(--dcm-font-weight-bold);

    /* ----------------------------------------------------------------
       CART BUTTON
       ---------------------------------------------------------------- */
    --dcm-cart-btn-bg-active: var(--dcm-color-primary);
    --dcm-cart-btn-bg-empty: var(--dcm-color-border);
    --dcm-cart-btn-text-active: var(--dcm-color-text-inverted);
    --dcm-cart-btn-text-empty: var(--dcm-color-text-primary);
    --dcm-cart-btn-width: 59px;
    --dcm-cart-btn-height: 32px;
    --dcm-cart-btn-width-desktop: 67px;
    --dcm-cart-btn-height-desktop: 44px;
    --dcm-cart-btn-font-weight: var(--dcm-font-weight-semibold);
    --dcm-cart-btn-border-radius: var(--dcm-radius-sm);
    --dcm-cart-btn-hover-bg: #00000033;
    --dcm-cart-btn-padding: 0 8px;
    --dcm-cart-btn-padding-desktop: 0 12px;
    --dcm-cart-btn-transition: all 0.2s;

    /* ----------------------------------------------------------------
       SEARCH
       ---------------------------------------------------------------- */
    --dcm-search-input-height: 40px;
    --dcm-search-input-border-color: var(--dcm-color-border);
    --dcm-search-input-border-radius: var(--dcm-radius-buttons);
    --dcm-search-input-font-family: var(--dcm-font-secondary);
    --dcm-search-input-padding-left: 40px;
    --dcm-search-input-focus-border-color: var(--dcm-color-focus);
    --dcm-search-input-focus-shadow: 0 1px 5px var(--dcm-color-focus-shadow);
    --dcm-search-input-transition: all 0.2s;
    --dcm-search-icon-color: var(--dcm-color-text-primary);
    --dcm-search-icon-left: 12px;
    --dcm-search-btn-font-size: var(--dcm-font-size-md);
    --dcm-search-btn-font-weight: var(--dcm-font-weight-bold);

    /* ----------------------------------------------------------------
       USER / ACCOUNT BUTTON
       ---------------------------------------------------------------- */
    --dcm-user-btn-font-size: var(--dcm-font-size-sm);
    --dcm-user-btn-font-weight: var(--dcm-font-weight-semibold);
    --dcm-user-btn-color: inherit;
    --dcm-user-btn-gap: 8px;
    --dcm-user-btn-gap-desktop: 12px;
    --dcm-user-btn-name-max-width: 12ch;
    --dcm-user-btn-name-color: var(--dcm-color-text-secondary);
    --dcm-user-btn-name-border-color: #d9d9d9;

    /* ----------------------------------------------------------------
       ACCOUNT POPOVER
       ---------------------------------------------------------------- */
    --dcm-account-popover-width-in: 187px;
    --dcm-account-popover-width-out: 225px;
    --dcm-account-popover-item-font-size: var(--dcm-font-size-md);
    --dcm-account-popover-item-line-height: 175%;
    --dcm-account-popover-item-padding-left: 30px;
    --dcm-account-popover-item-height: 45px;
    --dcm-account-popover-item-color: ${({ theme }) => theme.colors.grey[30]};
    --dcm-account-popover-item-hover-bg: ${({ theme }) => theme.colors.primaryGrey};
    --dcm-account-popover-name-height: 47px;
    --dcm-account-popover-name-padding: 5px 30px 0px;
    --dcm-account-popover-divider-color: var(--dcm-color-border);
    --dcm-account-popover-logout-color: #0b99e6;
    --dcm-account-popover-shadow: rgba(0, 0, 0, 0.2) 0px 5px 15px 0px;
    --dcm-account-popover-border-radius: 8px;

    /* ----------------------------------------------------------------
       LOGIN NUDGE
       ---------------------------------------------------------------- */
    --dcm-login-nudge-font-size: var(--dcm-font-size-md);
    --dcm-login-nudge-line-height: 20px;
    --dcm-login-nudge-text-color: var(--dcm-color-text-primary, ${({ theme }) => theme.colors.primaryBlack});
    --dcm-login-nudge-link-color: var(--dcm-color-primary);
    --dcm-login-nudge-social-btn-size: 32px;
    --dcm-login-nudge-social-btn-bg: var(--dcm-color-surface-muted);
    --dcm-login-nudge-social-btn-radius: 9999px;
    --dcm-login-nudge-social-icon-size: 16px;
    --dcm-login-nudge-gap: var(--dcm-space-2);
    --dcm-login-nudge-font-family: var(--dcm-font-primary);
    --dcm-login-nudge-bg: var(--dcm-color-surface);
    --dcm-login-nudge-shadow: 0 6px 20px 0 rgba(0, 0, 0, 0.25);
    --dcm-login-nudge-close-color: #5E6D79;
    --dcm-login-nudge-apple-icon-color: #2C3236;

    /* ----------------------------------------------------------------
       LOGGED-OUT CTA
       ---------------------------------------------------------------- */
    --dcm-logged-out-cta-bg: var(--dcm-color-surface-muted);
    --dcm-logged-out-cta-border-color: #e3e7e9;
    --dcm-logged-out-cta-border-radius: var(--dcm-radius-lg);
    --dcm-logged-out-cta-text-color: #2c3236;
    --dcm-logged-out-cta-subtext-color: #646d72;
    --dcm-logged-out-cta-link-color: var(--dcm-color-primary);

    /* ----------------------------------------------------------------
       BREADCRUMBS
       ---------------------------------------------------------------- */
    --dcm-breadcrumb-link-color: ${({ theme }) => theme.colors.grey[25]};
    --dcm-breadcrumb-active-color: var(--dcm-color-text-primary, ${({ theme }) => theme.colors.primaryBlack});
    --dcm-breadcrumb-separator-color: ${({ theme }) => theme.colors.grey[70]};

    /* ----------------------------------------------------------------
       CONTENT HEADER (category page heading + sort/layout controls)
       ---------------------------------------------------------------- */
    --dcm-content-header-text-color: var(--dcm-color-text-primary);
    --dcm-content-header-border-color: var(--dcm-color-border);
    --dcm-content-header-icon-color: var(--dcm-color-text-secondary);

    /* ----------------------------------------------------------------
       INLINE CATEGORIES LIST
       ---------------------------------------------------------------- */
    --dcm-inline-categories-heading-color: var(--dcm-color-text-primary);
    --dcm-inline-categories-divider-color: var(--dcm-color-border);
    --dcm-inline-categories-chip-bg: transparent;
    --dcm-inline-categories-chip-text-color: var(--dcm-color-text-primary);
    --dcm-inline-categories-chip-border-color: var(--dcm-color-border);
    --dcm-inline-categories-chip-hover-border-color: var(--dcm-color-text-secondary);

    /* ----------------------------------------------------------------
       SECTION TITLES
       ---------------------------------------------------------------- */
    --dcm-section-title-color: #2c3236;
    --dcm-section-subtitle-color: #828a8f;

    /* ----------------------------------------------------------------
       CATEGORIES
       ---------------------------------------------------------------- */
    --dcm-categories-card-bg: var(--dcm-color-surface);
    --dcm-categories-card-border-color: #e3e7e9;
    --dcm-categories-card-text-color: #121516;
    --dcm-categories-card-hover-shadow: 0 5px 14px rgba(0, 0, 0, 0.1);
    --dcm-categories-view-all-bg: #e3e7e9;
    --dcm-categories-view-all-color: #2c3236;

    /* ----------------------------------------------------------------
       BRANDS
       ---------------------------------------------------------------- */
    --dcm-brands-title-color: #242526;
    --dcm-brands-no-results-color: #999;
    --dcm-brands-card-bg: var(--dcm-color-surface);
    --dcm-brands-card-border-color: #e3e7e9;
    --dcm-brands-card-border-radius: 12px;
    --dcm-brands-card-shadow: 0 0 0 rgba(0, 0, 0, 0.1);
    --dcm-brands-card-hover-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
    --dcm-brands-card-name-color: var(--dcm-color-text-primary);
    --dcm-brands-card-placeholder-color: #485055;
    --dcm-brands-card-placeholder-bg: white;

    /* ----------------------------------------------------------------
       PRODUCT (shared card + detail page)
       ---------------------------------------------------------------- */
    --dcm-product-page-bg: var(--dcm-color-surface);
    --dcm-product-name-color: var(--dcm-color-text-primary);
    --dcm-product-brand-color: var(--dcm-color-text-secondary);
    --dcm-product-description-color: var(--dcm-color-text-primary);
    --dcm-product-subheading-color: var(--dcm-color-text-primary);
    --dcm-product-price-color: var(--dcm-color-primary);
    --dcm-product-price-strikethrough-color: var(--dcm-color-text-secondary);
    --dcm-product-price-muted-color: var(--dcm-color-text-muted);
    --dcm-product-option-bg: var(--dcm-color-surface-muted);
    --dcm-product-option-border-color: var(--dcm-color-border);
    --dcm-product-option-text-color: var(--dcm-color-text-primary);
    --dcm-product-input-bg: var(--dcm-color-surface);
    --dcm-product-input-border-color: var(--dcm-color-border);
    --dcm-product-detail-text-color: var(--dcm-color-text-primary);
    --dcm-product-detail-muted-color: var(--dcm-color-text-muted);
    --dcm-product-effect-bg: var(--dcm-color-surface-muted);
    --dcm-product-effect-text-color: var(--dcm-color-text-muted);
    --dcm-product-progress-bg: var(--dcm-color-surface-muted);
    --dcm-product-progress-fill: ${({ theme }) => theme.colors.green[45]};
    --dcm-product-accordion-icon-color: var(--dcm-color-text-muted);
    --dcm-product-chip-bg: var(--dcm-color-surface-muted);
    --dcm-product-chip-text-color: var(--dcm-color-text-primary);
    --dcm-product-chip-hover-bg: var(--dcm-color-surface-hover);

    /* ----------------------------------------------------------------
       CART DRAWER
       ---------------------------------------------------------------- */
    --dcm-cart-bg: var(--dcm-color-surface);
    --dcm-cart-content-bg: var(--dcm-color-surface-muted);
    --dcm-cart-text-color: var(--dcm-color-text-primary);
    --dcm-cart-text-muted-color: var(--dcm-color-text-secondary);
    --dcm-cart-border-color: var(--dcm-color-border);
    --dcm-cart-item-bg: var(--dcm-color-surface);
    --dcm-cart-item-border-color: var(--dcm-color-surface-muted);
    --dcm-cart-item-separator-color: var(--dcm-color-border);
    --dcm-cart-price-color: var(--dcm-color-text-primary);
    --dcm-cart-toggle-bg: var(--dcm-color-surface-muted);
    --dcm-cart-toggle-active-bg: var(--dcm-color-surface);
    --dcm-cart-backdrop-color: ${({ theme }) => theme.colors.whiteOverlay};
    --dcm-cart-banner-bg: #fae8ea;
    --dcm-cart-banner-text-color: #be0517;
    --dcm-cart-offers-bg: var(--dcm-color-surface);
    --dcm-cart-offers-text-color: #646d72;
    --dcm-cart-offers-divider-color: #f0f0f0;
    --dcm-cart-offers-progress-track-bg: #ceeedb;
    --dcm-cart-offers-applied-chip-bg: #d1efde;
    --dcm-cart-offers-accent-color: #00624a;
    --dcm-cart-offers-placeholder-color: #d2d7da;
    --dcm-cart-recommendations-bg: var(--dcm-cart-bg);
    --dcm-cart-recommendations-heading-color: var(--dcm-section-title-color);
    --dcm-cart-recommendations-border-color: ${({ theme }) => theme.colors.blueGrey[95]};
    --dcm-cart-recommendations-separator-bg: var(--dcm-cart-content-bg);
    --dcm-cart-recommendations-sponsored-gradient-color: var(--dcm-cart-content-bg);

    /* ----------------------------------------------------------------
       ADDED TO CART INTERSTITIAL
       ---------------------------------------------------------------- */
    --dcm-added-to-cart-bg: var(--dcm-color-surface);
    --dcm-added-to-cart-shadow: 0 0 20px 0 #00000026;
    --dcm-added-to-cart-border-color: #e3e7e9;
    --dcm-added-to-cart-heading-color: #121516;
    --dcm-added-to-cart-close-icon-color: #485055;

    /* ----------------------------------------------------------------
       CHECKOUT
       ---------------------------------------------------------------- */
    --dcm-checkout-heading-color: var(--dcm-color-text-secondary);
    --dcm-checkout-section-heading-color: #43474a;
    --dcm-checkout-section-border-color: ${({ theme }) => theme.colors.greyBorder};
    --dcm-checkout-text-color: #6d747b;
    --dcm-checkout-text-muted-color: #909ca7;
    --dcm-checkout-link-color: #0b99e6;
    --dcm-checkout-input-bg: var(--dcm-color-surface);
    --dcm-checkout-input-border-color: var(--dcm-color-border);
    --dcm-checkout-summary-bg: #f0f3f6;
    --dcm-checkout-summary-text-color: #606369;
    --dcm-checkout-summary-border-color: #d3dadf;
    --dcm-checkout-summary-total-color: #606369;
    --dcm-checkout-payment-tab-bg: var(--dcm-color-surface);
    --dcm-checkout-payment-tab-border-color: ${({ theme }) => theme.colors.grey[85]};
    --dcm-checkout-payment-content-bg: var(--dcm-color-surface);
    --dcm-checkout-disclaimer-color: #667882;
    --dcm-checkout-save-info-bg: #f0f3f6;
    --dcm-checkout-payment-icon-color: #a3b2c1;
    --dcm-checkout-payment-icon-bg: var(--dcm-color-surface);

    /* ----------------------------------------------------------------
       CAROUSEL BUTTONS
       ---------------------------------------------------------------- */
    --dcm-carousel-btn-bg: ${({ theme }) => theme.colors.grey[95]};
    --dcm-carousel-btn-hover-bg: ${({ theme }) => theme.colors.blueGrey[90]};
    --dcm-carousel-btn-shadow: 0 3px 5px 0 rgba(0, 0, 0, 0.17);
    --dcm-carousel-btn-arrow-color: ${({ theme }) => theme.colors.grey[35]};

    /* Standalone embedded carousel */
    --dcm-embedded-carousel-card-bg: #ffffff;
    --dcm-embedded-carousel-card-border-color: #e3e7e9;
    --dcm-embedded-carousel-card-name-color: #121516;
    --dcm-embedded-carousel-card-brand-color: #646d72;
    --dcm-embedded-carousel-card-price-color: #2c3236;
    --dcm-embedded-carousel-card-radius: var(--dct--customized-radius-lg);
    --dcm-embedded-carousel-button-radius: var(--dct--customized-radius-buttons);
    --dcm-embedded-carousel-gutter: 25px;
    --dcm-embedded-carousel-item-gap: 12px;
    --dcm-embedded-carousel-items-padding-y: 20px;
    --dcm-embedded-carousel-items-padding-x: 16px;
    --dcm-embedded-carousel-card-padding: 16px;

    /* ----------------------------------------------------------------
       STORE STATUS
       ---------------------------------------------------------------- */
    --dcm-status-font-size: var(--dcm-font-size-sm);
    --dcm-status-font-weight: var(--dcm-font-weight-semibold);
    --dcm-status-open-color: var(--dcm-color-status-open);
    --dcm-status-closed-color: var(--dcm-color-status-closed);
    --dcm-status-preorder-color: var(--dcm-color-status-preorder);
    --dcm-status-coming-soon-color: var(--dcm-color-status-coming-soon);
    --dcm-status-label-color: var(--dcm-color-text-secondary);
    --dcm-status-label-font-weight: var(--dcm-font-weight-bold);

    /* ----------------------------------------------------------------
       INFO BAR
       ---------------------------------------------------------------- */
    --dcm-infobar-bg: var(--dcm-color-surface);
    --dcm-infobar-text-color: var(--dcm-color-text-primary);
    --dcm-infobar-border-color: var(--dcm-color-border);
    --dcm-infobar-border-width: 1px;
    --dcm-infobar-padding-x: 12px;
    --dcm-infobar-padding-y: 8px;
    --dcm-infobar-gap: 20px;
    --dcm-infobar-font-size: var(--dcm-font-size-sm);

    /* ----------------------------------------------------------------
       MENU DRAWER
       ---------------------------------------------------------------- */
    --dcm-drawer-bg: var(--dcm-color-surface);
    --dcm-drawer-width: 100%;
    --dcm-drawer-accent-color: var(--dcm-color-primary);
    --dcm-drawer-close-btn-size: 46px;
    --dcm-drawer-close-btn-opacity: 0.7;
    --dcm-drawer-close-btn-color: ${({ theme }) => theme.colors.grey[55]};
    --dcm-drawer-close-btn-bar-size: 3px;
    --dcm-drawer-status-height: 62px;
    --dcm-drawer-status-font-size: var(--dcm-font-size-md);
    --dcm-drawer-status-padding: 25px;
    --dcm-drawer-status-bg: ${({ theme }) => theme.colors.primaryGrey};
    --dcm-drawer-tab-font-size: 16px;
    --dcm-drawer-tab-font-weight: bold;
    --dcm-drawer-tab-padding-top: 22px;
    --dcm-drawer-tab-padding-bottom: 14px;
    --dcm-drawer-tab-active-color: var(--dcm-color-primary);
    --dcm-drawer-tab-inactive-color: ${({ theme }) => theme.colors.grey[45]};
    --dcm-drawer-tab-indicator-max-width: 90px;
    --dcm-drawer-nav-padding: 16px 32px;
    --dcm-drawer-nav-gap: 16px;
    --dcm-drawer-nav-heading-font-size: var(--dcm-font-size-md);
    --dcm-drawer-nav-heading-font-weight: var(--dcm-font-weight-bold);
    --dcm-drawer-nav-heading-color: var(--dcm-color-text-primary);
    --dcm-drawer-nav-link-color: #646d72;
    --dcm-drawer-nav-link-font-size: var(--dcm-font-size-2xl);
    --dcm-drawer-nav-link-padding: 16px 0;

    /* ----------------------------------------------------------------
       FOOTER
       ---------------------------------------------------------------- */
    --dcm-footer-bg: var(--dcm-color-surface-hover);
    --dcm-footer-bg-sm: var(--dcm-color-surface);
    --dcm-footer-text-color: ${({ theme }) => theme.colors.grey[35]};
    --dcm-footer-disclaimer-margin: 50px auto;
    --dcm-footer-disclaimer-margin-sm: 25px auto 25px;
    --dcm-footer-padding: 0;
    --dcm-footer-border-top: none;

    /* ----------------------------------------------------------------
       PRIVACY FOOTER
       ---------------------------------------------------------------- */
    --dcm-privacy-footer-height: 30px;
    --dcm-privacy-footer-bg: white;
    --dcm-privacy-footer-border-color: ${({ theme }) => theme.colors.blueGrey[80]};
    --dcm-privacy-footer-shadow: 0px -2.62px 2px rgba(0, 0, 0, 0.042);
    --dcm-privacy-footer-text-color: ${({ theme }) => theme.colors.grey[35]};
    --dcm-privacy-footer-text-hover-color: #121516;
    --dcm-privacy-footer-font-size: var(--dcm-font-size-xs);
    --dcm-privacy-footer-divider-color: #d2d7da;
    --dcm-privacy-footer-z-index: 10;
    --dcm-privacy-footer-link-gap: 5px;

    /* ----------------------------------------------------------------
       MENU BANNER
       ---------------------------------------------------------------- */
    --dcm-banner-font-size: var(--dcm-font-size-sm);
    --dcm-banner-line-height: 1.65;
    --dcm-banner-padding: 24px;
    --dcm-banner-margin: 50px auto 60px;
    --dcm-banner-margin-sm: 25px auto 40px;
    --dcm-banner-border-radius: var(--dcm-radius-lg);
    --dcm-banner-max-width: var(--dct--customized-maxSiteWidth);
    --dcm-banner-collapsed-height: 150px;
    --dcm-banner-chevron-size: 16px;
    --dcm-banner-mask-height: 126px;

    /* ----------------------------------------------------------------
       MENU TYPE
       ---------------------------------------------------------------- */
    --dcm-menu-type-font-size: var(--dcm-font-size-sm);
    --dcm-menu-type-font-weight: var(--dcm-font-weight-bold);
    --dcm-menu-type-label-color: var(--dcm-color-text-secondary);
    --dcm-menu-type-height: 44px;
    --dcm-menu-type-margin-right: 30px;

    /* ----------------------------------------------------------------
       STATUS MENU SELECT
       ---------------------------------------------------------------- */
    --dcm-menu-select-bg: var(--dcm-color-surface);
    --dcm-menu-select-height: 43px;
    --dcm-menu-select-padding-x: 21px;

    /* ----------------------------------------------------------------
       STORE FRONT HEADER (legacy)
       ---------------------------------------------------------------- */
    --dcm-storefront-header-height: 72px;
    --dcm-storefront-header-border-color: #c5d0d7;
    --dcm-storefront-header-z-index: 10;
    --dcm-storefront-header-bg: var(--dcm-color-surface);
    --dcm-storefront-header-link-font-size: var(--dcm-font-size-sm);
    --dcm-storefront-header-link-gap: 12px;
    --dcm-storefront-header-divider-opacity: 0.5;
    --dcm-storefront-header-logo-height: 44px;
    --dcm-storefront-header-logo-margin-left: 2px;
    --dcm-storefront-header-padding-mobile: 0 2.5%;

    /* ----------------------------------------------------------------
       PLACEHOLDER / SKELETON
       ---------------------------------------------------------------- */
    --dcm-skeleton-bg: var(--dcm-color-surface-hover);
    --dcm-skeleton-border-color: #c5d0d7;
    --dcm-skeleton-border-radius: 4px;
    --dcm-skeleton-card-border-radius: 12px;
    --dcm-skeleton-shimmer-from: rgba(0, 0, 0, 0.06);
    --dcm-skeleton-shimmer-to: rgba(0, 0, 0, 0.1);
    --dcm-skeleton-shimmer-dark-from: rgba(255, 255, 255, 0.1);
    --dcm-skeleton-shimmer-dark-to: rgba(255, 255, 255, 0.2);
    --dcm-skeleton-animation-duration: 1.5s;
    --dcm-skeleton-logo-width: 120px;
    --dcm-skeleton-logo-height: 44px;
    --dcm-skeleton-nav-height: 16px;

    /* ----------------------------------------------------------------
       MY ACCOUNT LINK
       ---------------------------------------------------------------- */
    --dcm-account-link-font-size: var(--dcm-font-size-md);
    --dcm-account-link-margin-mobile: 0px 18px 0px 0px;
    --dcm-account-link-margin-desktop: 0px 22px;
    --dcm-account-link-icon-color: #b2b8c3;

    /* ----------------------------------------------------------------
       SKIP LINK
       ---------------------------------------------------------------- */
    --dcm-skip-link-bg: var(--dcm-color-primary);
    --dcm-skip-link-color: var(--dcm-color-text-inverted);
    --dcm-skip-link-padding: 4px 8px;
    --dcm-skip-link-z-index: 9999;

    /* ----------------------------------------------------------------
       FILTER SIDEBAR
       ---------------------------------------------------------------- */
    --dcm-filter-heading-color: var(--dcm-color-text-primary);
    --dcm-filter-text-color: var(--dcm-color-text-primary);
    --dcm-filter-border-color: var(--dcm-color-border);
    --dcm-filter-icon-color: var(--dcm-color-text-secondary);
    --dcm-filter-search-bg: var(--dcm-color-surface);
    --dcm-filter-search-border-color: var(--dcm-color-border);
    --dcm-filter-search-text-color: var(--dcm-color-text-primary);
    --dcm-filter-search-placeholder-color: var(--dcm-color-text-secondary);
    --dcm-filter-search-icon-color: var(--dcm-color-text-muted);
    --dcm-filter-search-focus-border-color: var(--dcm-color-focus);
    --dcm-filter-search-focus-shadow: 0 1px 5px var(--dcm-color-focus-shadow);

    /* ----------------------------------------------------------------
       MODAL (generic — used by all modals)
       ---------------------------------------------------------------- */
    --dcm-modal-bg: var(--dcm-color-surface);
    --dcm-modal-text-color: var(--dcm-color-text-primary);
    --dcm-modal-border-radius: 28px;
    --dcm-modal-shadow: 0 5px 15px 0 rgba(0, 0, 0, 0.14);
    --dcm-modal-footer-bg: var(--dcm-color-surface-muted);
    --dcm-modal-footer-text-color: var(--dcm-color-text-primary);
    --dcm-modal-close-color: var(--dcm-color-text-secondary);
    --dcm-modal-backdrop-color: rgba(0, 0, 0, 0.5);

    /* ----------------------------------------------------------------
       BOTTOM DRAWER
       ---------------------------------------------------------------- */
    --dcm-bottom-drawer-bg: var(--dcm-modal-bg);
    --dcm-bottom-drawer-text-color: var(--dcm-modal-text-color);
    --dcm-bottom-drawer-exit-color: var(--dcm-modal-close-color);

    /* ----------------------------------------------------------------
       AGE GATE MODAL
       ---------------------------------------------------------------- */
    --dcm-age-gate-modal-bg: var(--dcm-modal-bg);
    --dcm-age-gate-modal-text-color: var(--dcm-modal-text-color);
    --dcm-age-gate-modal-border-radius: var(--dcm-modal-border-radius);
    --dcm-age-gate-modal-shadow: var(--dcm-modal-shadow);
    --dcm-age-gate-modal-padding: 40px 47px 45px 47px;
    --dcm-age-gate-modal-max-width: 420px;
    --dcm-age-gate-heading-font-size: var(--dcm-font-size-2xl);
    --dcm-age-gate-heading-font-weight: var(--dcm-font-weight-bold);
    --dcm-age-gate-body-font-size: var(--dcm-font-size-md);
    --dcm-age-gate-body-color: #5d666d;
    --dcm-age-gate-terms-font-size: var(--dcm-font-size-md);
    --dcm-age-gate-link-color: var(--dcm-color-primary);
    --dcm-age-gate-btn-height: 49px;
    --dcm-age-gate-btn-font-size: var(--dcm-font-size-sm);
    --dcm-age-gate-btn-border-radius: var(--dcm-radius-buttons);
    --dcm-age-gate-remember-font-size: var(--dcm-font-size-xs);
    --dcm-age-gate-backdrop-color: rgba(0, 0, 0, 0.5);
    --dcm-age-gate-backdrop-blur: 8px;

    /* ----------------------------------------------------------------
       AGE VERIFICATION PAGE
       ---------------------------------------------------------------- */
    --dcm-age-gate-margin-top: 28px;
    --dcm-age-gate-padding: 0 15px;
    --dcm-age-gate-cta-height: 90px;
    --dcm-age-gate-cta-padding: 16px 50px;
    --dcm-age-gate-cta-copy-font-size: var(--dcm-font-size-sm);
    --dcm-age-gate-cta-btn-font-size: var(--dcm-font-size-xs);
    --dcm-age-gate-cta-btn-height: 29px;
    --dcm-age-gate-cta-btn-width: 87px;

    /* ----------------------------------------------------------------
       ORDER TYPE TILES
       ---------------------------------------------------------------- */
    --dcm-order-type-tile-bg: ${({ theme }) => theme.colors.primaryGrey};
    --dcm-order-type-tile-border-color: ${({ theme }) => theme.colors.blueGrey[80]};
    --dcm-order-type-tile-hover-bg: ${({ theme }) => theme.colors.blueGrey[95]};
    --dcm-order-type-tile-hover-border-color: ${({ theme }) => theme.colors.grey[70]};
    --dcm-order-type-tile-label-color: ${({ theme }) => theme.colors.grey[45]};
    --dcm-order-type-tile-subinfo-color: ${({ theme }) => theme.colors.grey[30]};
    --dcm-order-type-tile-subinfo-closed-color: ${({ theme }) => theme.colors.grey[60]};
    --dcm-order-type-tile-icon-color: #a3b2c1;
    --dcm-order-type-tile-icon-closed-color: #9fb3c3;
    --dcm-order-type-tile-selected-bg: ${({ theme }) => theme.colors.grey[95]};
    --dcm-order-type-tile-mobile-border-color: ${({ theme }) => theme.colors.grey[90]};
    --dcm-order-type-tile-mobile-label-color: ${({ theme }) => theme.colors.grey[35]};
    --dcm-order-type-modal-header-color: ${({ theme }) => theme.colors.grey[35]};

    /* ----------------------------------------------------------------
       COLLECTIONS
       ---------------------------------------------------------------- */
    --dcm-collections-description-color: #4b5257;
    --dcm-collections-timer-bg: #121516;
    --dcm-collections-timer-text-color: #ffffff;

    /* ----------------------------------------------------------------
       LOCATIONS
       ---------------------------------------------------------------- */
    --dcm-locations-heading-color: ${({ theme }) => theme.colors.grey[30]};
    --dcm-locations-text-color: ${({ theme }) => theme.colors.grey[45]};
    --dcm-locations-subtext-color: #46494c;
    --dcm-locations-link-color: var(--dcm-color-primary);
    --dcm-locations-card-bg: #f7f9fb;
    --dcm-locations-card-border-color: #cbd4d9;
    --dcm-locations-card-text-color: #6d747b;
    --dcm-locations-card-hover-bg: #ebf0f4;
    --dcm-locations-card-hover-border-color: #bec8cd;
    --dcm-locations-browser-bg: var(--dcm-color-surface);
    --dcm-locations-browser-text-color: #6d747b;
    --dcm-locations-marker-label-bg: var(--dcm-color-surface);
    --dcm-locations-marker-address-color: ${({ theme }) => theme.colors.grey[45]};
    --dcm-locations-scrollbar-color: ${({ theme }) => theme.colors.grey[45]};
    --dcm-locations-pin-color: var(--dcm-color-primary);
    --dcm-locations-pin-highlight-color: ${({ theme }) => theme.colors.blue[55]};
    --dcm-locations-close-hover-bg: ${({ theme }) => theme.colors.grey[45]};

    /* ----------------------------------------------------------------
       REFERRAL
       ---------------------------------------------------------------- */
    --dcm-referral-border-color: #e3e7e9;
    --dcm-referral-heading-color: ${({ theme }) => theme.colors.primaryBlack};
    --dcm-referral-subtext-color: #454e50;
    --dcm-referral-disclaimer-color: #646d72;
    --dcm-referral-disclaimer-underline-color: rgb(203 214 221);
    --dcm-referral-input-bg: #fcfdfe;
    --dcm-referral-input-text-color: #828a8f;
    --dcm-referral-input-border-color: #e3e7e9;
    --dcm-referral-qr-modal-bg: var(--dcm-color-surface);
    --dcm-referral-qr-heading-color: var(--dcm-color-text-primary);
    --dcm-referral-qr-text-color: ${({ theme }) => theme.colors.grey[50]};
    --dcm-referral-tab-inactive-bg: #e3e7e9;

    /* ----------------------------------------------------------------
       ACCOUNT
       ---------------------------------------------------------------- */
    --dcm-account-bg: ${({ theme }) => theme.colors.primaryGrey};
    --dcm-account-surface-bg: var(--dcm-color-surface);
    --dcm-account-surface-shadow: ${({ theme }) => theme.colors.blueGrey[95]};
    --dcm-account-nav-color: ${({ theme }) => theme.colors.grey[30]};
    --dcm-account-label-color: ${({ theme }) => theme.colors.grey[60]};
    --dcm-account-divider-color: ${({ theme }) => theme.colors.blueGrey[90]};
    --dcm-account-icon-fill: #eaeff2;
    --dcm-account-icon-accent: #bccad2;
    --dcm-account-icon-stroke: #5e6d79;

    /* ----------------------------------------------------------------
       SPECIALS / MULTI-TIER BADGE
       ---------------------------------------------------------------- */
    --dcm-specials-multi-tier-badge-bg: #00624a;
    --dcm-specials-multi-tier-badge-text-color: #fff;
    --dcm-specials-multi-tier-badge-font-size: 11px;
    --dcm-specials-multi-tier-badge-padding: 4px 8px;
    --dcm-specials-multi-tier-badge-z-index: 1;
    --dcm-specials-offer-card-multi-tier-badge-offset-top: 12px;
    --dcm-specials-offer-card-multi-tier-badge-offset-right: 12px;

    /* ----------------------------------------------------------------
       SPECIALS / GRID
       ---------------------------------------------------------------- */
    --dcm-specials-grid-max-width: calc(358px * 3 + 16px * 2);
    --dcm-specials-grid-min-column: 280px;

    /* ----------------------------------------------------------------
       STRAIN TYPE PILL
       ---------------------------------------------------------------- */
    --dct--customized-colors-strainTypes-cbd: ${({ theme }) =>
      theme.customized.colors.strainTypes?.cbd ||
      theme.customized.colors.strainTypes?.default ||
      DEFAULT_STRAIN_TYPE_PILL_BACKGROUND};
    --dct--customized-colors-strainTypes-cbg: ${({ theme }) =>
      theme.customized.colors.strainTypes?.cbg ||
      theme.customized.colors.strainTypes?.default ||
      DEFAULT_STRAIN_TYPE_PILL_BACKGROUND};
    --dct--customized-colors-strainTypes-high-cbd: ${({ theme }) =>
      theme.customized.colors.strainTypes?.highCBD ||
      theme.customized.colors.strainTypes?.default ||
      DEFAULT_STRAIN_TYPE_PILL_BACKGROUND};
    --dct--customized-colors-strainTypes-hybrid: ${({ theme }) =>
      theme.customized.colors.strainTypes?.hybrid ||
      theme.customized.colors.strainTypes?.default ||
      DEFAULT_STRAIN_TYPE_PILL_BACKGROUND};
    --dct--customized-colors-strainTypes-indica: ${({ theme }) =>
      theme.customized.colors.strainTypes?.indica ||
      theme.customized.colors.strainTypes?.default ||
      DEFAULT_STRAIN_TYPE_PILL_BACKGROUND};
    --dct--customized-colors-strainTypes-indica-hybrid: ${({ theme }) =>
      theme.customized.colors.strainTypes?.indicaHybrid ||
      theme.customized.colors.strainTypes?.default ||
      DEFAULT_STRAIN_TYPE_PILL_BACKGROUND};
    --dct--customized-colors-strainTypes-sativa: ${({ theme }) =>
      theme.customized.colors.strainTypes?.sativa ||
      theme.customized.colors.strainTypes?.default ||
      DEFAULT_STRAIN_TYPE_PILL_BACKGROUND};
    --dct--customized-colors-strainTypes-sativa-hybrid: ${({ theme }) =>
      theme.customized.colors.strainTypes?.sativaHybrid ||
      theme.customized.colors.strainTypes?.default ||
      DEFAULT_STRAIN_TYPE_PILL_BACKGROUND};
    --dct--customized-colors-strainTypes-seed: ${({ theme }) =>
      theme.customized.colors.strainTypes?.seed ||
      theme.customized.colors.strainTypes?.default ||
      DEFAULT_STRAIN_TYPE_PILL_BACKGROUND};
    --dct--customized-colors-strainTypes-thc: ${({ theme }) =>
      theme.customized.colors.strainTypes?.thc ||
      theme.customized.colors.strainTypes?.default ||
      DEFAULT_STRAIN_TYPE_PILL_BACKGROUND};

    --dcm-strain-type-pill-bg-cbd: var(--dct--customized-colors-strainTypes-cbd);
    --dcm-strain-type-pill-bg-cbg: var(--dct--customized-colors-strainTypes-cbg);
    --dcm-strain-type-pill-bg-high-cbd: var(--dct--customized-colors-strainTypes-high-cbd);
    --dcm-strain-type-pill-bg-hybrid: var(--dct--customized-colors-strainTypes-hybrid);
    --dcm-strain-type-pill-bg-indica: var(--dct--customized-colors-strainTypes-indica);
    --dcm-strain-type-pill-bg-indica-hybrid: var(--dct--customized-colors-strainTypes-indica-hybrid);
    --dcm-strain-type-pill-bg-sativa: var(--dct--customized-colors-strainTypes-sativa);
    --dcm-strain-type-pill-bg-sativa-hybrid: var(--dct--customized-colors-strainTypes-sativa-hybrid);
    --dcm-strain-type-pill-bg-seed: var(--dct--customized-colors-strainTypes-seed);
    --dcm-strain-type-pill-bg-thc: var(--dct--customized-colors-strainTypes-thc);

    --dcm-strain-type-pill-text-cbd: ${({ theme }) =>
      getStrainTypePillTextColor(
        theme.customized.colors.strainTypes?.cbd ||
          theme.customized.colors.strainTypes?.default ||
          DEFAULT_STRAIN_TYPE_PILL_BACKGROUND
      )};
    --dcm-strain-type-pill-text-cbg: ${({ theme }) =>
      getStrainTypePillTextColor(
        theme.customized.colors.strainTypes?.cbg ||
          theme.customized.colors.strainTypes?.default ||
          DEFAULT_STRAIN_TYPE_PILL_BACKGROUND
      )};
    --dcm-strain-type-pill-text-high-cbd: ${({ theme }) =>
      getStrainTypePillTextColor(
        theme.customized.colors.strainTypes?.highCBD ||
          theme.customized.colors.strainTypes?.default ||
          DEFAULT_STRAIN_TYPE_PILL_BACKGROUND
      )};
    --dcm-strain-type-pill-text-hybrid: ${({ theme }) =>
      getStrainTypePillTextColor(
        theme.customized.colors.strainTypes?.hybrid ||
          theme.customized.colors.strainTypes?.default ||
          DEFAULT_STRAIN_TYPE_PILL_BACKGROUND
      )};
    --dcm-strain-type-pill-text-indica: ${({ theme }) =>
      getStrainTypePillTextColor(
        theme.customized.colors.strainTypes?.indica ||
          theme.customized.colors.strainTypes?.default ||
          DEFAULT_STRAIN_TYPE_PILL_BACKGROUND
      )};
    --dcm-strain-type-pill-text-indica-hybrid: ${({ theme }) =>
      getStrainTypePillTextColor(
        theme.customized.colors.strainTypes?.indicaHybrid ||
          theme.customized.colors.strainTypes?.default ||
          DEFAULT_STRAIN_TYPE_PILL_BACKGROUND
      )};
    --dcm-strain-type-pill-text-sativa: ${({ theme }) =>
      getStrainTypePillTextColor(
        theme.customized.colors.strainTypes?.sativa ||
          theme.customized.colors.strainTypes?.default ||
          DEFAULT_STRAIN_TYPE_PILL_BACKGROUND
      )};
    --dcm-strain-type-pill-text-sativa-hybrid: ${({ theme }) =>
      getStrainTypePillTextColor(
        theme.customized.colors.strainTypes?.sativaHybrid ||
          theme.customized.colors.strainTypes?.default ||
          DEFAULT_STRAIN_TYPE_PILL_BACKGROUND
      )};
    --dcm-strain-type-pill-text-seed: ${({ theme }) =>
      getStrainTypePillTextColor(
        theme.customized.colors.strainTypes?.seed ||
          theme.customized.colors.strainTypes?.default ||
          DEFAULT_STRAIN_TYPE_PILL_BACKGROUND
      )};
    --dcm-strain-type-pill-text-thc: ${({ theme }) =>
      getStrainTypePillTextColor(
        theme.customized.colors.strainTypes?.thc ||
          theme.customized.colors.strainTypes?.default ||
          DEFAULT_STRAIN_TYPE_PILL_BACKGROUND
      )};
  }

  /* MUI Menu dropdowns render in a portal — override defaults */
  .MuiMenu-list {
    background-color: var(--dcm-product-input-bg) !important;
    color: var(--dcm-product-option-text-color) !important;
  }

  .MuiMenu-list .MuiMenuItem-root {
    background-color: var(--dcm-product-input-bg) !important;
    color: inherit !important;

    &:hover,
    &.Mui-selected {
      background-color: var(--dcm-color-surface-hover) !important;
    }
  }
`;
